import React, { useState } from 'react';
import { toast as message } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../utils/supabase';
import type { ActionDialogProps } from '../types';
import EnhancedDialog from '../../EnhancedDialog';

export const AchVerifyDialog: React.FC<ActionDialogProps> = ({
  isOpen,
  onClose,
  prefill,
}) => {
  const navigate = useNavigate();
  const [descriptorCode, setDescriptorCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!descriptorCode.trim()) return;
    setSubmitting(true);

    try {
      const { data, error } = await supabaseClient.functions.invoke('ach-mandate-verify', {
        body: {
          ach_mandate_id: Number(prefill.ach_mandate_id),
          descriptor_code: descriptorCode.trim().toUpperCase(),
        },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw error;

      message.success('Bank account verified — mandate is now active.');
      setDescriptorCode('');
      onClose();
      navigate(0 as any);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Verification failed';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setDescriptorCode('');
      onClose();
    }
  };

  return (
    <EnhancedDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Verify Bank Account"
      style={{ width: 450 }}
      canOutsideClickClose={!submitting}
    >
      <div className="sky-dialog-body">
        <div className="sky-callout" style={{ marginBottom: 16 }}>
          Check the bank statement for two small deposits from Stripe. Each deposit
          has a 6-character <strong>descriptor code</strong> (e.g. <code>SM11AA</code>).
          Enter either code below to verify the account.
        </div>

        <div className="sky-form-group">
          <label className="sky-form-label">Descriptor Code <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(required)</span></label>
          <input
            className="sky-field-input"
            value={descriptorCode}
            onChange={(e) => setDescriptorCode(e.target.value)}
            placeholder="e.g. SM11AA"
            style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)', fontSize: 16 }}
            autoFocus
          />
        </div>
      </div>

      <div className="sky-dialog-footer">
        <button className="sky-btn sky-btn-ghost" onClick={handleClose} disabled={submitting}>Cancel</button>
        <button
          className="sky-btn sky-btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !descriptorCode.trim()}
        >
          {submitting ? <span className="sky-spinner" /> : null}
          Verify
        </button>
      </div>
    </EnhancedDialog>
  );
};
